import { marketCellIndexFromGrid, SIXES_MARKET_CELL_COUNT } from "./sixesGridLayout";

/**
 * Sixes "buckets" — the 9 market groups a card cell can belong to, each with its own colour.
 *
 * A real fixture has 9 candidate buckets, but only **7 populate any one grid**:
 * a random flip picks one team's Runs bucket and the *other* team's Player bucket, dropping the
 * remaining two. Because the two Runs buckets (and the two Player buckets) are mutually exclusive,
 * each pair shares a colour — so the board always shows the same 7-colour palette.
 *
 * Seven distinct hues on the purple board. Mutually-exclusive Runs / Player pairs share a slot
 * colour (they never co-appear). Footer/status accents (#32F78D sets, #FC51BB wins, #D2FF00 lime)
 * sit outside this grid palette.
 */

export const SIXES_BUCKETS = [
    "team_h2h",
    "firsts",
    "team_a_runs",
    "team_b_runs",
    "innings",
    "team_a_player",
    "team_b_player",
    "match",
    "c8_own",
] as const;

export type SixesBucket = (typeof SIXES_BUCKETS)[number];

/** Buckets that get 6 cells on the grid (the rest get 4). */
export const SIXES_SIX_DRAW_BUCKETS: readonly SixesBucket[] = ["team_h2h", "c8_own"];

/** Per-bucket cell colour. The mutually-exclusive Runs / Player pairs share a colour. */
export const SIXES_BUCKET_COLORS: Record<SixesBucket, string> = {
    team_h2h: "#FC51BB",
    firsts: "#FFE033",
    team_a_runs: "#BF00FF", // Runs slot (home / team A)
    team_b_runs: "#BF00FF", // Runs slot (away / team B)
    innings: "#FF6B6B",
    team_a_player: "#00E5FF", // Player slot (home / team A)
    team_b_player: "#00E5FF", // Player slot (away / team B)
    match: "#FF8C00",
    c8_own: "#2D6BFF",
};

/** Human labels (legend / debugging). */
export const SIXES_BUCKET_LABELS: Record<SixesBucket, string> = {
    team_h2h: "Team H2H",
    firsts: "Firsts",
    team_a_runs: "Team A Runs",
    team_b_runs: "Team B Runs",
    innings: "Innings",
    team_a_player: "Team A Player",
    team_b_player: "Team B Player",
    match: "Match",
    c8_own: "C8 Own (Player H2H)",
};

const SIXES_BUCKET_SET = new Set<string>(SIXES_BUCKETS);

/** Neutral non-purple fallback for a cell we cannot classify (real cards should always classify). */
const SIXES_BUCKET_FALLBACK_COLOR = "#3A3F66";

/** Normalize a persisted or derived bucket id to a known Sixes bucket. */
export function normalizeSixesBucket(bucket: string | null | undefined): SixesBucket | null {
    const key = String(bucket || "").trim();
    if (!key) return null;
    return SIXES_BUCKET_SET.has(key) ? (key as SixesBucket) : null;
}

/** Resolve the cell base colour for a (possibly missing) bucket. */
export function sixesBucketColor(bucket: SixesBucket | string | null | undefined): string {
    const normalized = normalizeSixesBucket(bucket ?? null);
    return normalized ? SIXES_BUCKET_COLORS[normalized] : SIXES_BUCKET_FALLBACK_COLOR;
}

function normalizeTeamKey(value: string): string {
    return String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
}

function abbrevTeam(name: string): string {
    const words = String(name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (words.length === 0) return "";
    if (words.length === 1) {
        return words[0]!.replace(/[^A-Za-z0-9]/g, "").slice(0, 3).toUpperCase();
    }
    return words
        .map((w) => w.replace(/[^A-Za-z0-9]/g, ""))
        .filter(Boolean)
        .map((w) => w[0]!)
        .join("")
        .slice(0, 3)
        .toUpperCase();
}

function teamNameMentioned(text: string, teamName: string): boolean {
    const team = String(teamName || "").trim();
    if (!team) return false;
    const textLower = text.toLowerCase();
    const teamLower = team.toLowerCase();
    if (textLower.includes(teamLower)) return true;
    const textKey = normalizeTeamKey(text);
    const teamKey = normalizeTeamKey(team);
    if (textKey && teamKey && (textKey.includes(teamKey) || teamKey.includes(textKey))) return true;
    const abbr = abbrevTeam(team);
    if (abbr.length >= 2 && new RegExp(`\\b${abbr.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i").test(text)) {
        return true;
    }
    return false;
}

/** Which fixture side a sentence refers to. Home maps to team_a_* slots, away to team_b_*. */
function teamSideFromText(text: string, homeTeam = "", awayTeam = ""): "home" | "away" | null {
    const homeHit = teamNameMentioned(text, homeTeam);
    const awayHit = teamNameMentioned(text, awayTeam);
    if (homeHit && !awayHit) return "home";
    if (awayHit && !homeHit) return "away";
    return null;
}

function runsBucketForSide(side: "home" | "away" | null): SixesBucket | null {
    if (side === "away") return "team_b_runs";
    if (side === "home") return "team_a_runs";
    return null;
}

function playerBucketForSide(side: "home" | "away" | null): SixesBucket | null {
    if (side === "away") return "team_b_player";
    if (side === "home") return "team_a_player";
    return null;
}

function isPlayerPropSentence(sentence: string): boolean {
    const s = String(sentence || "").trim().toLowerCase();
    return (
        /will score (over|under) [\d.]+ runs$/.test(s) ||
        /will hit (over|under) [\d.]+ (fours|sixes)$/.test(s)
    );
}

export type SixesBucketFromSentenceOptions = {
    homeTeam?: string;
    awayTeam?: string;
};

/**
 * Classify a cheer sentence (as produced by `buildSixesCheerSentence`) into one of the 9 buckets.
 * Returns null when the sentence shape is unrecognised. (Demo cards assign buckets directly; this
 * path is the fallback for real API cards.)
 */
export function sixesBucketFromSentence(
    sentence: string,
    options: SixesBucketFromSentenceOptions = {},
): SixesBucket | null {
    const raw = String(sentence || "").trim();
    if (!raw) return null;
    const s = raw.toLowerCase();
    const homeTeam = String(options.homeTeam || "").trim();
    const awayTeam = String(options.awayTeam || "").trim();
    const side = teamSideFromText(raw, homeTeam, awayTeam);

    // C8 player-vs-player ("X will score more runs than Y").
    if (/will score more runs than /.test(s)) return "c8_own";

    // Team head-to-heads: match winner / toss / most boundaries / highest partnership / first over / 6 overs.
    if (/will win the toss$/.test(s)) return "team_h2h";
    if (/will win the match$/.test(s)) return "team_h2h";
    if (/will hit more sixes/.test(s) || /will hit most (sixes|fours)$/.test(s)) return "team_h2h";
    if (/will (have )?highest opening partnership$/.test(s)) return "team_h2h";
    if (/will win highest first over$/.test(s)) return "team_h2h";
    if (/will win highest first \d+ overs$/.test(s)) return "team_h2h";
    if (/will win highest first session$/.test(s)) return "team_h2h";
    if (/will (have|win) (the )?highest first \d+ overs( score)?$/.test(s)) return "team_h2h";
    if (/will have the highest runscorer$/.test(s)) return "team_h2h";
    if (/will have the highest wicket taker$/.test(s)) return "team_h2h";
    if (/—\s*team of highest runscorer$/i.test(raw)) return "team_h2h";
    if (/—\s*team of highest wicket taker$/i.test(raw)) return "team_h2h";

    // "Firsts": over-1 runs / off the first ball / fall of the first wicket / runs first delivery.
    if (/runs in over\s+1\b/.test(s)) return "firsts";
    if (/off (the )?first ball/.test(s)) return "firsts";
    if (/runs(?: off)? (?:first )?delivery/.test(s)) return "firsts";
    if (/first wicket - (over|under)/.test(s)) return "firsts";
    if (/(?:first wicket will fall|will lose their first wicket) (after|before) over/.test(s)) return "firsts";

    // Match totals ("a/no batter", "match will have …").
    if (/^(a|no) batter will score over [\d.]+ runs$/.test(s)) return "match";
    if (/^match highest individual score (over|under) [\d.]+$/.test(s)) return "match";
    if (/^match will have (over|under) [\d.]+/.test(s)) return "match";

    // Innings totals (per team) — legacy "X innings will have" and "{team} will have … wickets|fours|sixes".
    if (/ innings will have (over|under) [\d.]+/.test(s)) return "innings";
    if (/ will have (over|under) [\d.]+ (wickets|fours|sixes)\b/.test(s) && !/^match will have/.test(s)) {
        return "innings";
    }

    // Team runs: overs 2+ + session runs (per team, overs or balls). Over 1 is firsts (above).
    if (/runs in over|runs in the over|runs in first \d+ overs|runs in first \d+ balls|runs in the first session|session runs/.test(s)) {
        return runsBucketForSide(side);
    }

    // Player runs / boundaries (per team) — side may be resolved in a second pass when absent.
    if (isPlayerPropSentence(raw)) {
        return playerBucketForSide(side);
    }

    // "Over N — <title>" fallbacks for over/delivery runners.
    const dashIdx = raw.indexOf("—");
    const tail = dashIdx >= 0 ? raw.slice(dashIdx + 1).toLowerCase() : s;
    if (/runs(?: off)? (?:first )?delivery|\bdelivery\b/.test(tail)) {
        return "firsts";
    }
    if (/runs in over\s+1\b/.test(tail)) return "firsts";
    if (/runs in over/.test(tail)) {
        return runsBucketForSide(teamSideFromText(tail, homeTeam, awayTeam) ?? side);
    }
    if (/innings/.test(tail)) return "innings";
    if (/match/.test(tail)) return "match";

    return null;
}

function resolvePersistedBucket(persisted: string): SixesBucket | null {
    return normalizeSixesBucket(persisted);
}

/** Resolve per-cell buckets for a full API card (handles player props without team in the sentence). */
export function buildSixesBucketMapFromApiCells(
    cells: ReadonlyArray<{ row: number; col: number; sentence: string; bucket?: string | null }>,
    homeTeam: string,
    awayTeam: string,
): (SixesBucket | null)[] {
    const buckets: (SixesBucket | null)[] = Array.from({ length: SIXES_MARKET_CELL_COUNT }, () => null);

    for (const cell of cells) {
        const idx = marketCellIndexFromGrid(cell.row, cell.col);
        if (idx < 0) continue;
        // Prefer the bucket persisted at generation time; fall back to deriving it from the
        // sentence for legacy cards (cells whose bucket predates the column).
        const persisted = String(cell.bucket || "").trim();
        buckets[idx] =
            resolvePersistedBucket(persisted) ??
            sixesBucketFromSentence(cell.sentence, { homeTeam, awayTeam });
    }

    let homePlayerCount = 0;
    let awayPlayerCount = 0;
    for (const bucket of buckets) {
        if (bucket === "team_a_player") homePlayerCount += 1;
        if (bucket === "team_b_player") awayPlayerCount += 1;
    }
    const activePlayerBucket: SixesBucket =
        awayPlayerCount > homePlayerCount ? "team_b_player" : "team_a_player";

    for (const cell of cells) {
        const idx = marketCellIndexFromGrid(cell.row, cell.col);
        if (idx < 0 || buckets[idx] != null) continue;
        if (isPlayerPropSentence(cell.sentence)) {
            buckets[idx] = activePlayerBucket;
        }
    }

    return buckets;
}

/** Short bucket title for the cell detail modal header bar. */
export function sixesBucketDisplayLabel(
    bucket: SixesBucket | string | null | undefined,
    homeTeam = "",
    awayTeam = "",
): string {
    const normalized = normalizeSixesBucket(bucket ?? null);
    if (!normalized) return "Market";
    switch (normalized) {
        case "team_a_runs":
            return homeTeam ? `${abbrevTeam(homeTeam)} Runs` : "Team A Runs";
        case "team_b_runs":
            return awayTeam ? `${abbrevTeam(awayTeam)} Runs` : "Team B Runs";
        case "team_a_player":
            return homeTeam ? `${abbrevTeam(homeTeam)} Player` : "Team A Player";
        case "team_b_player":
            return awayTeam ? `${abbrevTeam(awayTeam)} Player` : "Team B Player";
        case "c8_own":
            return "Player H2H";
        default:
            return SIXES_BUCKET_LABELS[normalized];
    }
}
